import { useState } from "react";
import type { Category, ColumnId, GameState } from "@yamber/rules-engine";
import { DiceRow } from "./DiceRow";
import { ScoreSheet } from "./ScoreSheet";
import { AnnounceModal } from "./AnnounceModal";
import { GameSummary } from "./GameSummary";

interface GameScreenProps {
  gameState: GameState;
  playerId: string;
  onRoll: () => void;
  onToggleHold: (dieIndex: number) => void;
  onAnnounce: (category: Category) => void;
  onScore: (columnId: ColumnId, category: Category) => void;
  onReset: () => void;
}

export function GameScreen({
  gameState,
  playerId,
  onRoll,
  onToggleHold,
  onAnnounce,
  onScore,
  onReset,
}: GameScreenProps) {
  const [showAnnounceModal, setShowAnnounceModal] = useState(false);
  const [showSummary, setShowSummary] = useState(true);

  const player = gameState.players.find((p) => p.id === playerId);
  if (!player) {
    return <div className="game-screen__error">Player not found</div>;
  }

  const turn = player.currentTurn;
  const dice = turn?.dice ?? [1, 1, 1, 1, 1];
  const holds = turn?.holds ?? [false, false, false, false, false];
  const rollCount = turn?.rollCount ?? 0;
  const announcedCategory = turn?.announcedCategory ?? null;

  const isGameOver = gameState.status === "finished";
  const canRoll = !isGameOver && rollCount < 3;
  const canAnnounce = rollCount <= 1 && !announcedCategory;
  const hasAnnounceColumn = Object.values(player.sheet.columns).some(
    (col) => col.type === "announce"
  );

  const handleAnnounce = (category: Category) => {
    onAnnounce(category);
    setShowAnnounceModal(false);
  };

  if (isGameOver) {
    return (
      <>
        {showSummary && (
          <GameSummary
            templateId={gameState.templateId}
            sheet={player.sheet}
            totalScore={player.totalScore}
            playerName="You"
            onClose={() => setShowSummary(false)}
            onNewGame={onReset}
          />
        )}
        <div className="game-screen game-screen--finished">
          <h2 className="game-screen__title">Game Over!</h2>
          <div className="game-screen__final-score">
            <span className="game-screen__final-label">Final Score</span>
            <span className="game-screen__final-value">{player.totalScore}</span>
          </div>
          <ScoreSheet
            sheet={player.sheet}
            dice={dice}
            rollCount={0}
            announcedCategory={null}
            onScoreCell={() => {}}
          />
          <div className="game-screen__actions-row">
            <button
              type="button"
              className="game-screen__share-btn"
              onClick={() => setShowSummary(true)}
            >
              Share Score
            </button>
            <button type="button" className="game-screen__new-game" onClick={onReset}>
              New Game
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="game-screen" role="main" aria-label="Game in progress">
      <div className="game-screen__header" role="status" aria-live="polite">
        <div className="game-screen__info">
          <span className="game-screen__roll-count" aria-label={`Roll ${rollCount} of 3`}>
            Roll {rollCount}/3
          </span>
          {announcedCategory && (
            <span className="game-screen__announced" aria-label={`Announced category: ${announcedCategory}`}>
              Announced: {announcedCategory}
            </span>
          )}
        </div>
        <span className="game-screen__score" aria-label={`Current score: ${player.totalScore} points`}>
          Score: {player.totalScore}
        </span>
      </div>

      <div className="game-screen__dice-area" aria-label="Dice and controls">
        <DiceRow
          dice={dice}
          holds={holds}
          rollCount={rollCount}
          onToggleHold={onToggleHold}
        />

        <div className="game-screen__actions" role="group" aria-label="Game actions">
          <button
            type="button"
            className="game-screen__roll-btn"
            onClick={onRoll}
            disabled={!canRoll}
            aria-label={rollCount === 0 ? "Roll dice" : `Roll again, ${3 - rollCount} rolls remaining`}
          >
            {rollCount === 0 ? "Roll Dice" : `Roll Again (${3 - rollCount} left)`}
          </button>

          {hasAnnounceColumn && canAnnounce && (
            <button
              type="button"
              className="game-screen__announce-btn"
              onClick={() => setShowAnnounceModal(true)}
              aria-haspopup="dialog"
              aria-label="Announce a category"
            >
              Announce
            </button>
          )}
        </div>
      </div>

      <ScoreSheet
        sheet={player.sheet}
        dice={dice}
        rollCount={rollCount}
        announcedCategory={announcedCategory}
        onScoreCell={onScore}
      />

      <AnnounceModal
        open={showAnnounceModal}
        onAnnounce={handleAnnounce}
        onCancel={() => setShowAnnounceModal(false)}
      />
    </div>
  );
}
